import { useState, useEffect } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area } from 'recharts'
import {
  Calendar,
  Download,
  TrendUp,
  TrendDown,
  Users,
  CreditCard,
  Eye,
  Money,
  ChartBar,
  FileText,
  ChartLineUp,
  CurrencyDollar,
  ArrowUp,
  ArrowDown
} from 'phosphor-react'
import { toast } from 'react-hot-toast'
import { getConfig } from '../config/constants'

const config = getConfig()
const API_BASE_URL = config.API_URL

// Hook para autenticação
const useAuth = () => {
  const token = localStorage.getItem('admin_token')
  return { token }
}

interface ReportData {
  overview: {
    totalRevenue: number
    newUsers: number
    activeSubscriptions: number
    conversionRate: number
    totalUsers: number
    activeUsers: number
  }
  revenueData: Array<{
    month: string
    revenue: number
    subscriptions: number
    users: number
  }>
  planDistribution: Array<{
    name: string
    value: number
    color: string
  }>
  userGrowthData: Array<{
    date: string
    users: number
    activeUsers: number
  }>
}

export default function Reports() {
  const { token } = useAuth()
  const [selectedPeriod, setSelectedPeriod] = useState('30d')
  const [selectedReport, setSelectedReport] = useState('overview')
  const [loading, setLoading] = useState(true)
  const [reportData, setReportData] = useState<ReportData | null>(null)

  // Buscar dados dos relatórios
  const fetchReportData = async () => {
    try {
      setLoading(true)
              const response = await fetch(`${API_BASE_URL}/admin/reports?period=${selectedPeriod}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Erro ao buscar dados dos relatórios')
      }

      const data = await response.json()
      setReportData(data)
    } catch (error) {
      console.error('Erro ao buscar relatórios:', error)
      toast.error('Erro ao carregar relatórios')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchReportData()
  }, [selectedPeriod])

  const handleExportReport = (reportType: string) => {
    // TODO: Implementar exportação real
    toast.success(`Relatório ${reportType} exportado com sucesso!`)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatPercentage = (value: number, total: number) => {
    return ((value / total) * 100).toFixed(1)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    )
  }

  if (!reportData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Erro ao carregar dados dos relatórios</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relatórios</h1>
          <p className="text-gray-600">Análise detalhada de performance e métricas</p>
        </div>
        <div className="flex items-center space-x-4">
          <select 
            className="input w-40"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            <option value="7d">7 dias</option>
            <option value="30d">30 dias</option>
            <option value="90d">90 dias</option>
            <option value="1y">1 ano</option>
          </select>
          <button
            onClick={() => handleExportReport('geral')}
            className="btn btn-primary btn-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar Relatório
          </button>
        </div>
      </div>

      {/* Seletor de Relatório */}
      <div className="card card-body">
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'overview', label: 'Visão Geral', icon: ChartBar },
            { id: 'revenue', label: 'Receita', icon: Money },
            { id: 'users', label: 'Usuários', icon: Users },
            { id: 'subscriptions', label: 'Assinaturas', icon: CreditCard },
            { id: 'conversion', label: 'Conversão', icon: TrendUp },
          ].map((report) => (
            <button
              key={report.id}
              onClick={() => setSelectedReport(report.id)}
              className={`btn btn-sm ${selectedReport === report.id ? 'btn-primary' : 'btn-secondary'}`}
            >
              <report.icon className="h-4 w-4 mr-2" />
              {report.label}
            </button>
          ))}
        </div>
      </div>

      {/* Relatório de Visão Geral */}
      {selectedReport === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="card card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Receita Total</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(reportData.overview.totalRevenue)}
                  </p>
                </div>
                <Money className="h-8 w-8 text-green-600" />
              </div>
              <div className="mt-2 text-sm text-gray-500">
                <span className="text-green-600">+12.5%</span> vs mês anterior
              </div>
            </div>
            <div className="card card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Novos Usuários</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {reportData.overview.newUsers}
                  </p>
                </div>
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <div className="mt-2 text-sm text-gray-500">
                <span className="text-blue-600">+8.2%</span> vs mês anterior
              </div>
            </div>
            <div className="card card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Assinaturas Ativas</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {reportData.overview.activeSubscriptions}
                  </p>
                </div>
                <CreditCard className="h-8 w-8 text-purple-600" />
              </div>
              <div className="mt-2 text-sm text-gray-500">
                <span className="text-purple-600">+15.3%</span> vs mês anterior
              </div>
            </div>
            <div className="card card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Taxa de Conversão</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {reportData.overview.conversionRate}%
                  </p>
                </div>
                <TrendUp className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="mt-2 text-sm text-gray-500">
                <span className="text-red-600">-2.1%</span> vs mês anterior
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <div className="card-header flex justify-between items-center">
                <h3 className="text-lg font-medium">Crescimento da Receita</h3>
                <button onClick={() => handleExportReport('receita')} className="btn btn-secondary btn-sm">
                  <FileText className="h-4 w-4 mr-1" />
                  Exportar
                </button>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={reportData.revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Area type="monotone" dataKey="revenue" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card">
              <div className="card-header flex justify-between items-center">
                <h3 className="text-lg font-medium">Distribuição de Planos</h3>
                <button onClick={() => handleExportReport('planos')} className="btn btn-secondary btn-sm">
                  <FileText className="h-4 w-4 mr-1" />
                  Exportar
                </button>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={reportData.planDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {reportData.planDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Relatório de Usuários */}
      {selectedReport === 'users' && (
        <div className="space-y-6">
          <div className="card">
            <div className="card-header flex justify-between items-center">
              <h3 className="text-lg font-medium">Crescimento de Usuários</h3>
              <button onClick={() => handleExportReport('usuarios')} className="btn btn-secondary btn-sm">
                <FileText className="h-4 w-4 mr-1" />
                Exportar
              </button>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={reportData.userGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="users" stroke="#3b82f6" strokeWidth={2} name="Total de Usuários" />
                  <Line type="monotone" dataKey="activeUsers" stroke="#10b981" strokeWidth={2} name="Usuários Ativos" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card card-body">
              <h4 className="font-medium text-gray-900 mb-2">Novos Usuários (30 dias)</h4>
              <p className="text-3xl font-bold text-blue-600">{reportData.overview.newUsers}</p>
              <p className="text-sm text-gray-500">+15.3% vs período anterior</p>
            </div>
            <div className="card card-body">
              <h4 className="font-medium text-gray-900 mb-2">Usuários Ativos</h4>
              <p className="text-3xl font-bold text-green-600">{reportData.overview.activeUsers}</p>
              <p className="text-sm text-gray-500">
                {reportData.overview.totalUsers > 0 
                  ? ((reportData.overview.activeUsers / reportData.overview.totalUsers) * 100).toFixed(1)
                  : 0}% do total
              </p>
            </div>
            <div className="card card-body">
              <h4 className="font-medium text-gray-900 mb-2">Taxa de Retenção</h4>
              <p className="text-3xl font-bold text-purple-600">78.5%</p>
              <p className="text-sm text-gray-500">+2.1% vs período anterior</p>
            </div>
          </div>
        </div>
      )}

      {/* Relatório de Conversão */}
      {selectedReport === 'conversion' && (
        <div className="space-y-6">
          <div className="card">
            <div className="card-header flex justify-between items-center">
              <h3 className="text-lg font-medium">Funil de Conversão</h3>
              <button onClick={() => handleExportReport('conversao')} className="btn btn-secondary btn-sm">
                <FileText className="h-4 w-4 mr-1" />
                Exportar
              </button>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reportData.revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="users" fill="#e5e7eb" name="Visitas" />
                  <Bar dataKey="subscriptions" fill="#3b82f6" name="Cadastros" />
                  <Bar dataKey="revenue" fill="#10b981" name="Conversões" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card card-body">
              <h4 className="font-medium text-gray-900 mb-4">Métricas de Conversão</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total de Usuários</span>
                  <span className="font-medium">{reportData.overview.totalUsers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Assinaturas Ativas</span>
                  <span className="font-medium">{reportData.overview.activeSubscriptions}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Conversão Total</span>
                  <span className="font-medium text-green-600">
                    {reportData.overview.conversionRate}%
                  </span>
                </div>
              </div>
            </div>

            <div className="card card-body">
              <h4 className="font-medium text-gray-900 mb-4">Distribuição de Planos</h4>
              <div className="space-y-3">
                {reportData.planDistribution.map((plan, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-gray-600">{plan.name}</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full" 
                          style={{ 
                            width: `${(plan.value / reportData.overview.totalUsers) * 100}%`,
                            backgroundColor: plan.color
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium">
                        {reportData.overview.totalUsers > 0 
                          ? ((plan.value / reportData.overview.totalUsers) * 100).toFixed(1)
                          : 0}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 